"use client";

import Image from "next/image";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import React, {
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "./rooms.css";
import {
  Room,
  addSpaces,
  calculateMandatoryTaxes,
  generatePersons,
  getCancellationPolicyText,
  getHotelCharges,
  getMyRoomSummary,
} from "@/utils/helper";
import { RoomSummary } from "./RoomSummary";
import {
  getHotelLoading,
  saveBookingPayload,
} from "@/lib/features/hotel/hotelSlice";
import { InfoCircle } from "react-bootstrap-icons";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
} from "reactstrap";
import CancellationPolicy from "../CancellationPolicy";
import { getUserCurrentCountry } from "@/lib/features/home/homeSlice";
import { useSession } from "next-auth/react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Loader } from "@/components/ui";
import { map, filter, pick, isPlainObject, isArray } from "lodash-es";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
import { ReactSelectField } from "@/components/ui";
import { Formik, FormikProps } from "formik";
import RoomFilters from "./RoomFilters";
import RoomCard from "./RoomCard";
import {
  transformMealPlanOptions,
  mealPlanMapping,
} from "@/utils/mealPlanUtils";
import FilterHeader from "./FilterHeader";
import { ADD_TO_CART_EVENT, trackEvent } from "@/utils/gtm";
interface Person {
  title: string;
  name: string;
  surname: string;
  type: string;
  age?: number;
}

interface RoomItem {
  room_reference: string;
  paxes: Person[];
}

type RoomTypeGroups = {
  [room_type: string]: any;
};

interface FormValues {
  meal_plan: string[];
  free_cancellation: string;
  room_type: string[];
}

export const Rooms = ({ loading }: { loading: boolean }) => {
  const [roomTypeOptions, setRoomTypeOptions] = useState<any>([]);
  const [mealPlanOptions, setMealPlanOptions] = useState<any>([]);
  const [roomType, setRoomType] = useState<any>("");
  const [mealPlan, setMealPlan] = useState<any>("");
  const [freeCancellation, setFreeCancellation] = useState<any>("");
  const [allRooms, setAllRooms] = useState<RoomTypeGroups>({});
  const [isRoomSelected, setIsRoomSelected] = useState<boolean>(false);
  const { data: session, status } = useSession();
  const user: any = session?.user;
  const [modal, setModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>(null);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);



  const toggle = (data: any) => {
    setModalData(data);
    setModal(!modal);
  };

  const router = useRouter();
  const dispatch = useAppDispatch();
  const query: any = useSearchParams();

  const parsedPayload: any = useMemo(() => {
    return query ? parseQueryString(query) : null;
  }, [query]);

  const hotelId: any = parseInt(parsedPayload?.hotel_code);
  const hotelDetail = useAppSelector((state) => state.hotel.hotel);
  const userNationality = useAppSelector(getUserCurrentCountry);
  const hotelLoading = loading;
  const hotel = hotelDetail[hotelId]?.hotel ?? "";
  const searchId = hotelDetail[hotelId]?.search_id;
  const totalRooms = parseInt(parsedPayload?.guests?.length);
  const [selectedRooms, setSelectedRooms] = useState<Room[]>([]);

  const groupedByRoomType = useMemo(() => {
    const roomTypeGroups: { [room_type: string]: any[] } = {};
    const mealPlansSet = new Set();
    hotel?.rates?.length > 0 &&
      hotel?.rates.forEach((item: any) => {
        const roomType = item?.rooms?.length > 0 && item?.rooms[0]?.room_type;
        if (!roomType) return;

        if (!roomTypeGroups[roomType]) {
          roomTypeGroups[roomType] = [];
        }
        roomTypeGroups[roomType].push(item);
        mealPlansSet.add(item?.boarding_details[0]);
      });
    const sortedRoomTypes = Object.keys(roomTypeGroups);

    const uniqueRoomTypeArray = [
      // { label: "Select All", value: "" },
      ...Array.from(sortedRoomTypes).map((item) => ({
        label: item,
        value: item,
      })),
    ];
    const mealPlanArray = [
      // { label: "Select All", value: "" },
      ...Array.from(mealPlansSet)
        .map((item: any) => ({
          label: item,
          value: item,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
    setRoomTypeOptions(uniqueRoomTypeArray);
    setMealPlanOptions(mealPlanArray);
    setAllRooms(roomTypeGroups);
    return roomTypeGroups;
  }, [hotel]);

  const reserveRooom = useCallback(
    (room: Room) => {
      const {
        group_code,
        payment_type,
        rate_key,
        rate_type,
        room_code,
        rooms,
        rate_comments,
      } = room;
      const {
        checkin,
        checkout,
        no_of_adults,
        no_of_children,
        no_of_hotels,
        no_of_nights,
        no_of_rooms,
        search_id,
      } = hotelDetail[hotelId];
      const searchId = search_id;
      const city_code = hotel?.city_code;
      const { total, mandatoryTax } = calculateMandatoryTaxes(room);

      /*const isInfantConvertedToChild =
        rate_comments?.pax_comments ==
        "For this rate the infants will be converted to children with age 1."
          ? true
          : false;
     console.log("isInfantConvertedToChild", isInfantConvertedToChild);
   
      const items = rooms?.map((room: Room, index: number) => {
        const { room_reference, no_of_adults } =
          room;
        const adults = generatePersons(no_of_adults, "AD");
        // const childrens = generatePersons(no_of_children, "CH", children_ages);
        const children_ages = parsedPayload?.guests?.[index]?.children_ages?.filter((age: any) => age !== 0);
        const no_of_children = children_ages?.length || 0;
        const childrens = generatePersons(no_of_children, "CH", children_ages);
        return {
          ...(isInfantConvertedToChild == true && parsedPayload?.guests?.[index]?.no_of_infants && {
            no_of_infants: parsedPayload?.guests?.[index]?.no_of_infants,
          }),
          room_reference: room_reference,
          paxes: [...adults, ...childrens],
        };
      });*/
      const supplierRooms = Array.isArray(rooms) ? rooms : [];
      const guestRooms = Array.isArray(parsedPayload?.guests) ? parsedPayload.guests : [];

      const getSupplierRoomSignature = (supplierRoom: any) => {
        const adultsCount = Number(supplierRoom?.no_of_adults || 0);
        const childrenCount =
          supplierRoom?.no_of_children != null
            ? Number(supplierRoom?.no_of_children || 0)
            : Array.isArray(supplierRoom?.children_ages)
              ? supplierRoom.children_ages.length
              : 0;
        return `${adultsCount}|${childrenCount}`;
      };

      const usedSupplierRoomIndexes = new Set<number>();
      let items = guestRooms
        .map((guest: any) => {
          const children_ages = (guest?.children_ages || []).map((age: number) => (age === 0 ? 1 : age));
          const guestSignature = `${Number(guest?.adults || 0)}|${children_ages.length}`;

          let matchedIndex = supplierRooms.findIndex(
            (supplierRoom: any, supplierRoomIndex: number) =>
              !usedSupplierRoomIndexes.has(supplierRoomIndex) &&
              getSupplierRoomSignature(supplierRoom) === guestSignature
          );

          if (matchedIndex === -1) {
            matchedIndex = supplierRooms.findIndex(
              (_: any, supplierRoomIndex: number) => !usedSupplierRoomIndexes.has(supplierRoomIndex)
            );
          }

          if (matchedIndex === -1) {
            return null;
          }

          usedSupplierRoomIndexes.add(matchedIndex);
          const matchedSupplierRoom = supplierRooms[matchedIndex];

          const adults = generatePersons(Number(guest?.adults || 0), "AD");
          const childrens = generatePersons(children_ages.length, "CH", children_ages);

          return {
            room_reference: matchedSupplierRoom?.room_reference,
            paxes: [...adults, ...childrens],
          };
        })
        .filter(Boolean);

      if (items.length === 0 && supplierRooms.length > 0) {
        items = supplierRooms.map((supplierRoom: any) => {
          const supplierChildrenAges = Array.isArray(supplierRoom?.children_ages)
            ? supplierRoom.children_ages
            : [];
          const supplierChildrenCount =
            supplierRoom?.no_of_children != null
              ? Number(supplierRoom?.no_of_children || 0)
              : supplierChildrenAges.length;
          return {
            room_reference: supplierRoom?.room_reference,
            paxes: [
              ...generatePersons(Number(supplierRoom?.no_of_adults || 0), "AD"),
              ...generatePersons(supplierChildrenCount, "CH", supplierChildrenAges),
            ],
          };
        });
      }
      
      // console.log("items",items);
      const roomItems = [
        {
          rate_key: rate_key,
          room_code: room_code,
          rooms: items,
        },
      ];
      const payload: any = {
        rate_type,
        search_id: searchId,
        hotel_code: hotelId,
        city_code,
        group_code,
        checkin,
        checkout,
        payment_type: payment_type[0],
        holder: {
          title: "",
          name: user?.first_name || "",
          surname: user?.last_name || "",
          email: user?.email || "",
          phone_number: "",
          client_nationality: userNationality,
          pan_number: "",
          pan_company_name: "",
        },
        booking_items: roomItems,
        booking_comments: "",
        agent_reference: "",
        billing_address: {
          name: "",
          line1: "",
          line2: "",
          city: "",
          state: "",
          country: "",
          postal_code: "",
        },
        coupon_id: "",
        coupon_applied: "",
        applicable_amount:""
      };
      // console.log("Booking Payload For Single Room:", payload);
     
      const bookingObj: any = { room, payload, hotel: hotelDetail[hotelId] };
      dispatch(saveBookingPayload(bookingObj));
      // console.log("Seelcted hotelDetail[hotelId]", hotelDetail[hotelId]);
      const itemsData = {
        item_id:  hotelDetail[hotelId]?.hotel?.hotel_code,
        item_name: hotelDetail[hotelId]?.hotel?.name,
        price: room?.priceWithMarkup,
        markup: hotelDetail[hotelId]?.hotel?.markup,  
        index: 0,
        item_category: hotelDetail[hotelId]?.hotel?.accommodation_type_sub_name || "",
        location_id: hotelDetail[hotelId]?.hotel?.city_code,
        selectedRoom: {
          room_type: room?.rooms[0]?.room_type || "",
          description: room?.rooms[0]?.description || "",
          no_of_adults: room?.rooms[0]?.no_of_adults || "",
          no_of_children: room?.rooms[0]?.no_of_children || 0,
          no_of_rooms: room?.rooms[0]?.no_of_rooms || "",
          boarding_details: room?.boarding_details || ""
        }
      }
      const gtmData = {
        ecommerce: {
          currency: room?.currency,
          value: room?.priceWithMarkup,
          items: itemsData
        }
      }
      // console.log("gtmData add to cart", gtmData)
      trackEvent(ADD_TO_CART_EVENT, gtmData);
      redirectToBooking(hotelId);
    },
    [hotelDetail, hotelId, hotel, userNationality, user, parsedPayload]
  );

  const getTax = useCallback((room: any) => {
    return getHotelCharges(room);
  }, []);

  const redirectToBooking = useCallback(
    (hotel_code: string) => {
      const payload: any = {
        payload: parsedPayload,
      };
      console.log("payloadString", parsedPayload);
      const queryString: any = makeQueryString(parsedPayload);
      console.log("queryString", queryString);
      setIsRoomSelected(false);
      router.push(`/booking?${queryString}`);
    },
    [router, parsedPayload]
  );

  const handleRoomTypeChange = useCallback((value: any) => {
    setIsFiltering(true);
    setRoomType(value);
  }, []);

  const handleMealPlanChange = useCallback((value: any) => {
    setIsFiltering(true);
    setMealPlan(value);
  }, []);

  const handleFreeCancellationChange = useCallback((value: string) => {
    setIsFiltering(true);
    setFreeCancellation(value);
  }, []);

  const onFilterReset = useCallback(
    (resetForm: FormikProps<FormValues>["resetForm"]) => {
      setRoomType([]);
      setMealPlan([]);
      setFreeCancellation("");
      setAllRooms(groupedByRoomType); // Reset to original unfiltered rooms
      resetForm(); // Reset Formik state to initial values
    },
    [groupedByRoomType]
  );


  useEffect(() => {
    let filteredRooms: RoomTypeGroups = {};

    if (Object.entries(groupedByRoomType).length > 0) {
      Object.entries(groupedByRoomType).forEach(([key, rooms]) => {
        let filtered = rooms;

        // Apply room type filter (supports multiple room types)
        if (roomType && roomType.length > 0 && Array.isArray(roomType)) {
          if (!roomType.includes(key)) return;
        } else if (
          roomType &&
          typeof roomType === "string" &&
          roomType !== ""
        ) {
          if (key !== roomType) return;
        }

        // Apply meal plan filter (supports multiple meal plans)
        /*if (mealPlan && mealPlan.length > 0 && Array.isArray(mealPlan)) {
          filtered = filtered.filter((room) =>
            mealPlan.includes(room?.boarding_details[0])
          );
        } else if (mealPlan && typeof mealPlan === 'string' && mealPlan !== '') {
          filtered = filtered.filter(
            (room) => room?.boarding_details[0] === mealPlan
          );
        }*/
        // Apply meal plan filter (supports multiple meal plans)
        if (mealPlan && mealPlan.length > 0 && Array.isArray(mealPlan)) {
          filtered = filtered.filter((room) => {
            const boardingDetail = room?.boarding_details?.[0] || "";
            // Match any equivalent value from the mapping for each selected meal plan
            return mealPlan.some((plan) =>
              mealPlanMapping[plan]?.includes(boardingDetail)
            );
          });
        } else if (
          mealPlan &&
          typeof mealPlan === "string" &&
          mealPlan !== ""
        ) {
          filtered = filtered.filter((room) => {
            const boardingDetail = room?.boarding_details?.[0] || "";
            // Match any equivalent value from the mapping
            return mealPlanMapping[mealPlan]?.includes(boardingDetail);
          });
        }

        // Apply free cancellation filter
        if (freeCancellation && freeCancellation !== "") {
          filtered = filtered.filter((room) =>
            freeCancellation === "yes" ? room?.non_refundable === false : true
          );
        }

        // Add filtered rooms to result
        if (filtered.length > 0) {
          filteredRooms[key] = filtered;
        }
      });
    }

    setAllRooms(filteredRooms);
    setTimeout(() => {
      setIsFiltering(false);
    }, 500);
  }, [roomType, mealPlan, freeCancellation, groupedByRoomType]);

  const getRoomSummary = useCallback(() => {
    const roomSummary = getMyRoomSummary(hotelDetail[hotelId]);
    return roomSummary;
  }, [hotelDetail, hotelId]);

  const amenities: any[] = roomTypeOptions;
  const [showAll, setShowAll] = useState<boolean>(false);
  const maxLength = amenities?.length > 50 ? 18 : 9;
  const visibleAmenities = showAll ? amenities : amenities?.slice(0, maxLength);
  const toggleShowAll = () => setShowAll(!showAll);

  // Transform the meal plan options
  const allMealPlanOptions = transformMealPlanOptions(mealPlanOptions);

  return (
    <>
      {isFiltering && <Loader show={isFiltering} content={"Please wait..."} />}

      {isRoomSelected && (
        <Loader
          show={isRoomSelected}
          content={"Selecting room please wait..."}
        />
      )}
      {modalData && (
        <Modal isOpen={modal} toggle={() => toggle(null)} fade={false}>
          <ModalHeader toggle={() => toggle(null)}>
            Cancellation Policy
          </ModalHeader>
          <ModalBody>
            {modalData?.non_refundable == false ? (
              <CancellationPolicy data={modalData} />
            ) : (
              "This booking is non-refundable and cannot be amended or modified. If you fail to arrive or cancel, no refund will be given."
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
              onClick={() => toggle(null)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <div className="row x-gap-5 y-gap-10 justify-between items-end room_filter_border mb-20">
        <Formik<FormValues>
          enableReinitialize={true}
          initialValues={{
            meal_plan: [],
            free_cancellation: "",
            room_type: [],
          }}
          onSubmit={() => {
            console.log("Submitted");
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => {
            return (
              <>
                <FilterHeader onClearAll={() => onFilterReset(resetForm)} />
{/* {JSON.stringify(values)} */}
                <div className="col-12">
                  <Form className="row gx-3 gy-2 align-items-center ">
                    {/* <div className="col-md-12 col-12">
                    <div className="d-flex flex-wrap gap-2">
                      <span className="text-16 fw-bold">Room Type:</span>{" "}
                      <RoomFilters
                        name="room_type"
                        role="checkbox"
                        isMulti={true}
                        options={visibleAmenities}
                        onValueChange={(value) => {
                          handleRoomTypeChange(value);
                        }}
                      />
                      {amenities?.length > maxLength && (
                        <button
                          type="button"
                          onClick={toggleShowAll}
                          className="d-block text-12 text-blue-1 fw-500 underline mt-10 align-items-center"
                        >
                          {showAll ? "Show Less" : "Show More"}
                        </button>
                      )}
                    </div>

                  </div> */}

                    <div className="col-md-12 col-12">
                      <div className="d-flex flex-wrap gap-2">
                        <RoomFilters
                          name="meal_plan"
                          role="checkbox"
                          isMulti={true}
                          options={allMealPlanOptions}
                          onValueChange={(value) => {
                            handleMealPlanChange(value);
                          }}
                        />
                        <RoomFilters
                          name="free_cancellation"
                          role="checkbox"
                          options={[
                            { value: "yes", label: "Free Cancellation" },
                          ]}
                          onValueChange={(value: any) => {
                            handleFreeCancellationChange(value);
                          }}
                        />
                      </div>
                    </div>
                  </Form>
                </div>
              </>
            );
          }}
        </Formik>
      </div>

      <div className="col-auto">
        <div className="sectionTitle -md">
          <h6 className="sectionTitle__title text-12">
            {Object.entries(allRooms)?.length} Room types
          </h6>
        </div>
      </div>
      <RoomCard
        loading={hotelLoading}
        allRooms={allRooms}
        getRoomSummary={getRoomSummary}
        getCancellationPolicyText={getCancellationPolicyText}
        getTax={getTax}
        toggle={toggle}
        reserveRooom={reserveRooom}
        setIsRoomSelected={setIsRoomSelected}
      />
    </>
  );
};
